"use client";

import {
  lazy,
  Suspense,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { usePathname } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { AuthGuard } from "@/components/AuthGuard";
import { KnowledgeWarmup } from "@/components/files-browser/KnowledgeWarmup";
import { FilePreviewOverlay } from "@/components/files-browser/FilePreviewOverlay";
import { ShareDialogHost } from "@/components/sharing/ShareDialogHost";
import { ContentSkeleton } from "@/components/ContentSkeleton";
import { LeftSidebar, SIDEBAR_ROUTES } from "@/components/LeftSidebar";
import MobileHeader from "@/components/MobileHeader";
import { useRouteWarmer } from "@/hooks/useRouteWarmer";
import { useNavTiming } from "@/lib/nav-timing";
import { CommandPalette } from "@/components/CommandPalette";
import { EmbeddedBrowserRouteGuard } from "@/components/browser/EmbeddedBrowserRouteGuard";
import { BackspaceNavGuard } from "@/components/BackspaceNavGuard";
import { CreditWarningToast } from "@/components/CreditWarningToast";
import { CreditExhaustedModal } from "@/components/CreditExhaustedModal";
import { PhoneNudge } from "@/components/phone/PhoneNudge";
import { CreationViewerModal } from "@/components/creations/CreationViewerModal";
import { ResumeErrorToast } from "@/components/ResumeErrorToast";
import { InteractionErrorToast } from "@/components/InteractionErrorToast";
import { AdminMessageBanner } from "@/components/AdminMessageBanner";
import { UpdatingCapsule } from "@/components/UpdatingCapsule";
import { ConnectivityCapsule } from "@/components/ConnectivityCapsule";
import { ComputerSoundPill } from "@/components/computer/ComputerSoundPill";
import { useComputerSoundStore } from "@/stores/computerSoundStore";
import { watchPersonActivity } from "@/lib/person-activity";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Dock } from "@/components/home-briefing/Dock";
import { LineActions } from "@/components/composer/live/LineActions";
import { DockActionRelay } from "@/components/composer/live/DockActionRelay";
import { ScreenBeacon } from "@/components/composer/ScreenBeacon";
import {
  isDesktopShell,
  installExternalOpenInterceptor,
} from "@/lib/desktop-shell";
import { subscribeEmbeddedBrowserSurface } from "@/lib/embedded-browser";
import { useComputerStore } from "@/stores/computerStore";
import { destinationById, destinationOfPath } from "@bash/shared";
import { useSupportStore } from "@/stores/supportStore";
import { useTaskStore } from "@/stores/taskStore";
import { useBrowserStore } from "@/stores/browserStore";
import { DiagnosticBoundary } from "@/components/DiagnosticBoundary";
import { useIsMobile } from "@/hooks/useIsMobile";
import { setCurrentUserId } from "@/lib/clientIdentity";
import { traceFlash } from "@/lib/flash-trace";
import { startFlashRecorder } from "@/lib/flash-report";

// The computer's sound player rides the WebRTC client, which only a browser
// pane needs: load it once a pane has shown (the pane already loaded that
// client) or the sound is on, so every other page skips the module.
const ComputerSoundPlayer = lazy(() =>
  import("@/components/computer/ComputerSoundPlayer").then((m) => ({
    default: m.ComputerSoundPlayer,
  }))
);

/**
 * The sidebar item a path lights (spec 2026-09-17 §5.3, R-P-7): the destination the
 * path is on, then its root ancestor, then that root's first path segment. The
 * Brain's sections and pages are Knowledge; a task and the new-task screen are
 * Home; a marketplace detail page is Abilities; a path the registry does not know
 * is Home, as before. Exported for its table pin.
 */
export function deriveCurrentView(pathname: string): string {
  let d = destinationOfPath(pathname);
  if (!d) return "home";
  while (d.parent) {
    const parent = destinationById(d.parent);
    if (!parent) break;
    d = parent;
  }
  const root = d.route.split("?")[0].split("/").filter(Boolean)[0] ?? "home";
  return root === "task" ? "home" : root;
}

function deriveActiveView(
  pathname: string,
  selectedTaskId: string | null
): "home" | "newTask" | "conversation" {
  // For task routes, derive from STORE not URL — we use history.replaceState
  // / pushState directly for state-driven URL updates (see task/layout.tsx
  // nav effect comment), so usePathname() may be stale. The store is the
  // source of truth for whether the user is in a conversation or new-task mode.
  if (pathname === "/task" || pathname.startsWith("/task/")) {
    return selectedTaskId ? "conversation" : "newTask";
  }
  return "home";
}

/**
 * The task whose Computer is ON SCREEN, else null — what the sidebar obeys.
 *
 * The panel mounts in the task layout for the SELECTED task alone
 * (`showComputerPanel` there is `isOpen(currentTaskId)`), so that is the only
 * Computer a person can be looking at. `activeView === "conversation"` already
 * means "on a task route with a task selected", derived from the store because
 * the URL can lag (see deriveActiveView).
 */
export function computerTaskInView(
  activeView: "home" | "newTask" | "conversation",
  selectedTaskId: string | null,
  isOpen: (taskId: string) => boolean
): string | null {
  if (activeView !== "conversation" || !selectedTaskId) return null;
  return isOpen(selectedTaskId) ? selectedTaskId : null;
}

/**
 * The authenticated in-app shell: left sidebar, mobile header, global toasts,
 * and an AuthGuard-wrapped content region. Rendered for the `(app)` route group
 * and — for logged-in users only — by the public `(marketplace)` group via
 * `MarketplaceChrome`, so signed-in users keep their sidebar on /skills,
 * /agents, /abilities instead of the anon marketplace top bar.
 */
// Sidebar destinations (single source: the sidebar's own NAV_DEFS) plus the
// routes reached programmatically — the task stage (task cards), the account
// pages (profile card target + credit panels), support.
const WARM_ROUTES = [
  ...SIDEBAR_ROUTES,
  "/task",
  "/account/profile",
  "/account/credits",
  "/support",
];

export function AppShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const { signOut, user } = useAuth();
  const [showLeftSidebar, setShowLeftSidebar] = useState(false);
  const isMobile = useIsMobile();

  const currentView = deriveCurrentView(pathname);
  const selectedTaskId = useTaskStore((s) => s.selectedTaskId);
  const activeView = deriveActiveView(pathname, selectedTaskId);
  // The Computer holds the sidebar only while it is in front of you — never
  // one a background task opened on a screen you are not on (founder 09-04).
  const rawComputerInView = useComputerStore((s) =>
    computerTaskInView(activeView, selectedTaskId, s.isOpen)
  );
  // computerStore's persist middleware rehydrates from localStorage
  // synchronously at import time — before React's first CLIENT render, but
  // the server never has that value, so it always renders with the default
  // (empty) store. Cold-loading a task whose Computer was left open in a
  // previous session (sidebarBootstrapScript's own doc comment calls this out
  // as "the one case left") makes the client's hydration pass see
  // computerInView=true a beat before the server's HTML ever could — and
  // since LeftSidebar switches element TYPE (a home link vs. an icon button)
  // on this value, that beat is a genuine hydration mismatch, not just the
  // cosmetic width correction the CSS-var script was reasoning about. Hold
  // it at the SSR-safe default (null) until after mount, same pattern as
  // useIsMobile / LeftSidebar's own userExpanded restore.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const computerInView = mounted ? rawComputerInView : null;
  const supportUnread = useSupportStore((s) => s.getUnreadCount());
  const soundPlayerWanted = useComputerSoundStore((s) => s.on || s.panes > 0);
  const isOnTask = pathname.startsWith("/task/") || pathname === "/task";

  // Keep every navigation destination warm so clicks commit their URL from
  // cache. Sidebar routes come from the sidebar's own definition (rendering
  // and warming share one source, so they can't drift); the extras are
  // destinations reached programmatically, with no <Link> in the viewport to
  // warm them. The nav_timing beacon is the drift detector: a route going
  // slow in that dashboard means it belongs here or needs a real <Link>.
  useRouteWarmer(WARM_ROUTES);

  // Field guardrail: every navigation's click→URL-commit→paint timing lands in
  // PostHog (nav_timing), so "clicks got slow again" is a dashboard alert, not
  // a founder discovery weeks later.
  useNavTiming();

  const userInfo = useMemo(() => {
    if (!user) return null;
    return {
      id: user.id,
      name: user.user_metadata?.full_name || user.user_metadata?.name,
      email: user.email,
      avatarUrl: user.user_metadata?.avatar_url || user.user_metadata?.picture,
    };
  }, [user]);

  // user:signed_up is SERVER-emitted at the users-row first insert
  // (signup-notify.service.ts) — exactly once per account, however long
  // onboarding takes. The old web-side 10-minute created_at heuristic lived
  // here and undercounted slow onboarders.

  // Wrap signOut for diagnostics identity; the PostHog identity reset lives
  // inside AuthContext.signOut — the seam every sign-out path funnels through.
  const handleSignOut = useCallback(() => {
    setCurrentUserId(undefined);
    signOut();
  }, [signOut]);

  // Sidebar navigation is real <Link>s now (the Link navigates); this only
  // handles the post-click side effect.
  const handleNavLinkClick = useCallback(() => {
    if (isMobile) {
      setShowLeftSidebar(false);
    }
  }, [isMobile]);

  // A remount of the shell swaps the whole page out and back, which is the
  // one seam that would flash EVERY screen. Nothing records it today, so a
  // second 'mount' in the flash-trace buffer is the only way to see one.
  // The flash recorder starts first (once per page) so it hears this mount
  // and ships the next disruption to the server (flash-report.ts).
  useEffect(() => {
    startFlashRecorder();
    traceFlash("mount");
  }, []);

  // The person's last input or navigation: an hour without either stops the
  // computer's sound (ComputerSoundPlayer), so a forgotten stream cannot keep
  // a paid box awake.
  useEffect(() => watchPersonActivity(), []);

  // Update tab title with unread support messages
  useEffect(() => {
    const base = "Bash";
    document.title = supportUnread > 0 ? `(${supportUnread}) ${base}` : base;
  }, [supportUnread]);

  // The PostHog identify lives in AuthContext, which every route (onboarding
  // included) sits under; the shell only exposes the user id to non-React
  // diagnostics (reportError / error boundaries).
  useEffect(() => {
    if (!user) return;
    setCurrentUserId(user.id);
  }, [user]);

  // Auto-collapse the left sidebar whenever we land on (or transition to) mobile.
  useEffect(() => {
    if (isMobile) setShowLeftSidebar(false);
  }, [isMobile]);

  // Let nested components (e.g. TaskHeaderBar on mobile) open the left sidebar
  // without having to prop-drill setShowLeftSidebar through the task layout.
  // Mirrors the existing 'toggle-context-panel' pattern used by the panel toggle.
  useEffect(() => {
    const openSidebar = () => setShowLeftSidebar(true);
    window.addEventListener("open-left-sidebar", openSidebar);
    return () => window.removeEventListener("open-left-sidebar", openSidebar);
  }, []);

  // Diagnostic: capture React errors at the window level so an automated
  // test (or a user with DevTools closed) can still read the full stack
  // trace via console.log lookup. The Next.js route-level error.tsx only
  // gets the synthetic `{error, reset}` pair; it can't see the React
  // component stack. Logging here gives us the original `error.stack`
  // string at the moment of throw — when source maps are on (this PR),
  // those frames map back to original source:line numbers.
  //
  // Filters to React #300 / #418 specifically to keep noise low — those
  // are the two errors we're actively hunting on long browser tasks.
  useEffect(() => {
    const SERVER_URL =
      process.env.NEXT_PUBLIC_SERVER_URL || "http://localhost:3005";
    const ship = (payload: Record<string, unknown>) => {
      try {
        fetch(`${SERVER_URL}/api/diag/react-error`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          keepalive: true,
          credentials: "omit",
        }).catch(() => {});
      } catch {
        /* never break the page on diagnostics */
      }
    };
    const handler = (ev: ErrorEvent) => {
      const m = ev.error?.message ?? ev.message ?? "";
      if (/#300|#418|react\.dev\/errors\/30/.test(m)) {
        const payload = {
          kind: "window.error",
          msg: m,
          stack: ev.error?.stack ?? ev.filename + ":" + ev.lineno,
          url: window.location.href,
          at: new Date().toISOString(),
        };
        console.error("[react-error-capture]", payload);
        ship(payload);
      }
    };
    const rejHandler = (ev: PromiseRejectionEvent) => {
      const m =
        (ev.reason instanceof Error ? ev.reason.message : String(ev.reason)) ||
        "";
      if (/#300|#418|react\.dev\/errors\/30/.test(m)) {
        const payload = {
          kind: "window.unhandledrejection",
          msg: m,
          stack: ev.reason instanceof Error ? ev.reason.stack : undefined,
          url: window.location.href,
          at: new Date().toISOString(),
        };
        console.error("[react-error-capture-rejection]", payload);
        ship(payload);
      }
    };
    window.addEventListener("error", handler);
    window.addEventListener("unhandledrejection", rejHandler);
    return () => {
      window.removeEventListener("error", handler);
      window.removeEventListener("unhandledrejection", rejHandler);
    };
  }, []);

  // In the desktop shell (Tauri injects window.__TAURI__), flag <html> so the desktop-only
  // chrome applies: a top inset that clears the macOS traffic lights + a draggable title
  // strip. Also route external `_blank` opens (Composio OAuth, downloads, link previews)
  // to the real browser — the WKWebView has no tab surface, so they otherwise open an
  // invisible window. Both are inert in a normal browser, where __TAURI__ is undefined.
  useEffect(() => {
    if (typeof window === "undefined" || !isDesktopShell()) return;
    document.documentElement.classList.add("is-desktop-shell");
    return installExternalOpenInterceptor();
  }, []);

  // Desktop shell: the runner's loopback surface hint (relayed by the shell
  // as a LOCAL Tauri event — never the server socket, which is dead whenever
  // the app is occluded). React exactly like a live browser tool-fire: track
  // the browser session for the task + open the Computer panel's Browser tab.
  // The shell re-emits a pending hint on window focus, so a webview that
  // slept through the live emit catches up the moment the user looks.
  useEffect(() => {
    if (typeof window === "undefined" || !isDesktopShell()) return;
    return subscribeEmbeddedBrowserSurface(({ taskId }) => {
      useBrowserStore.getState().openPanelForTask(taskId);
      useComputerStore.getState().onAgentBrowserActivity(taskId);
    });
  }, []);

  // Desktop shell: the WKWebView has no browser chrome, so ⌘R does nothing by
  // default. The shell loads the web remotely (staging.vestra.ai), so ⌘R is how
  // you pick up a fresh web deploy without relaunching the app (founder-reported
  // gap, 2026-07-14). Capture-phase so app-level shortcuts can't swallow it.
  // Inert on web, where a real browser already owns ⌘R.
  useEffect(() => {
    if (typeof window === "undefined" || !isDesktopShell()) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey && (e.key === "r" || e.key === "R")) {
        e.preventDefault();
        window.location.reload();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  return (
    <div className="app-layout flex flex-col min-h-0 h-full bg-bg-base max-md:flex-col md:flex-row">
      {/* Desktop shell only: a transparent strip over the top inset so the window can be
          dragged / double-click-zoomed by its top edge (CSS gates it to html.is-desktop-shell;
          the native traffic lights render above the webview and stay clickable). */}
      <div
        data-tauri-drag-region
        className="desktop-drag-strip hidden"
        aria-hidden="true"
      />
      {/* Global ⌘K command palette — portals to body, so it sits anywhere here. */}
      <CommandPalette />
      {/* Desktop shell: hide the embedded browser view when a navigation
          leaves it without a mounted pane (renders nothing; inert on web). */}
      <EmbeddedBrowserRouteGuard />
      {/* Desktop shell: suppress WKWebView's Backspace-outside-an-input =
          history-back default, which exits the workspace to /home (renders
          nothing; inert on web). */}
      <BackspaceNavGuard />
      {/* Mobile Header — hidden on task pages where TaskHeaderBar carries
          the hamburger + title + controls inline (avoids two stacked bars) */}
      {isMobile && !isOnTask && (
        <MobileHeader onOpenSideBar={() => setShowLeftSidebar(true)} />
      )}

      {/* Left Sidebar */}
      {isMobile ? (
        <Dialog open={showLeftSidebar} onOpenChange={setShowLeftSidebar}>
          <DialogContent
            position="left-always"
            hideCloseButton
            className="sidebar-left h-screen p-0 xs:p-0 border-none w-fit bg-transparent shadow-none"
          >
            <LeftSidebar
              currentView={currentView}
              activeView={activeView}
              computerInView={computerInView}
              onLinkNavigate={handleNavLinkClick}
              user={userInfo}
              onSignOut={handleSignOut}
            />
          </DialogContent>
        </Dialog>
      ) : (
        <div className="sidebar-left h-screen h-dvh shrink-0 relative">
          <LeftSidebar
            currentView={currentView}
            activeView={activeView}
            computerInView={computerInView}
            onLinkNavigate={handleNavLinkClick}
            user={userInfo}
            onSignOut={handleSignOut}
          />
        </div>
      )}

      {/* Main Content — AuthGuard wraps only this region so sidebar/header
            paint immediately. Full-screen takeover states (beta gate,
            maintenance, server-unavailable) replace ONLY this subtree — the
            chrome siblings here (sidebar, header, toasts, Dock) keep rendering
            — so AuthGuard mounts them inside .gate-takeover (z-1200), which
            must out-stack every fixed chrome sibling in this file. The
            reconnection loader overlays the panel center during socket
            reconnection. */}
      <AuthGuard fallback={<ContentSkeleton />}>
        {/* Warm the Knowledge root's queries app-wide (post-gate) so
                    opening Knowledge paints from cache, not a fetch staircase. */}
        <KnowledgeWarmup />
        {/* Universal file preview — one overlay instance for every grid
                    (Knowledge folders, Media, Creations, the task panel). */}
        <FilePreviewOverlay />
        {/* The one share dialog (Train B), opened from any surface. */}
        <ShareDialogHost />
        {/* Bash's phone, the first open after launch (spec 2026-10-14 §3.1):
            post-gate, so only a signed-in, onboarded person is ever asked. */}
        <PhoneNudge />
        <DiagnosticBoundary name="AppChildren">{children}</DiagnosticBoundary>
      </AuthGuard>

      {/* Global Toasts & Modals */}
      {/* Deploy capsule: every (app) surface, including mid-turn (task page). */}
      <UpdatingCapsule />
      <ConnectivityCapsule />
      {/* The computer's sound plays here, above every page, so it keeps
          playing when the person looks elsewhere in the app. */}
      <ComputerSoundPill />
      {soundPlayerWanted && (
        <Suspense fallback={null}>
          <ComputerSoundPlayer />
        </Suspense>
      )}
      <AdminMessageBanner />
      <CreditWarningToast />
      <CreditExhaustedModal />
      <CreationViewerModal />
      <ResumeErrorToast />
      <InteractionErrorToast />

      {/* The dock: sticky composer pill on every (app) screen except /task
              (self-hiding + fixed-position; D10). */}
      <Dock />
      {/* The Line moves you (spec 2026-09-17 §5.4): performs a spoken
          navigation on every app route, the task page and support included. */}
      <LineActions />
      {/* The shell's Dock window forwards its spoken navigation here (spec 2026-09-22 §4). */}
      <DockActionRelay />
      {/* The app tells the server its screen (spec 2026-09-18, R-L1): a task that
          rests while they are looking at it says nothing. Once, on every app route. */}
      <ScreenBeacon />
    </div>
  );
}
